<template>
  <div class="flex flex-col p-4 relative" :style="{ height: getHeight() }">
    <PlanOverlay
      :visible="cashPerm == 'none'"
      :reason="cashPerm == 'none' && isSubUser ? 'permission' : 'plan'"
    />
    <!-- Header -->
    <div class="flex mb-0 gap-2 items-center">
      <h2 class="text-xl">Du</h2>
      <input class="basicInput" 
        type="date" 
        v-model="startDate"
        @keydown.prevent   
        @paste.prevent
        @wheel.prevent
        required
      />
      <h2 class="text-xl">au</h2>
      <input class="basicInput" 
        type="date" 
        v-model="endDate"
        @keydown.prevent   
        @paste.prevent
        @wheel.prevent
        required
      />
    </div>

    <!-- Contenu -->
    <div class="flex flex-col gap-4 !pb-8">
      <div class="w-full !h-[500px] mt-4">
        <PaymentChart class="h-full" :payments="filteredPayments" :startDate="startDate" :endDate="endDate"/>
      </div>
      <div class="w-full flex gap-6 ml-1">
        <div class="h-full w-full flex flex-col">
          <div class="flex justify-between items-center mb-2">
            <label class="font-bold">Entrée</label>
            <button @click="negative = false; popupVisible = true" class="callToAction mx-0 !px-4" v-if="cashPerm == 'modification'">Ajouter</button>
          </div>
          <PaymentList :payments="filteredPayments.positivePayments" @openPayment="selectedPayment = $event; popupVisible = true" /> 
        </div>
        <div class="h-full border border-gray-300"></div>
        <div class="h-full w-full flex flex-col mr-1">
          <div class="flex justify-between items-center mb-2">
            <label class="font-bold">Sortie</label>
            <button @click="negative = true; popupVisible = true" class="callToAction mx-0 !px-4" v-if="cashPerm == 'modification'">Ajouter</button>
          </div>
          <PaymentList :payments="filteredPayments.negativePayments" @openPayment="selectedPayment = $event; popupVisible = true" /> 
        </div>
      </div>
    </div>
    
    <!-- Popup payment -->
    <PopupPayment
        :visible="popupVisible" 
        :payment="selectedPayment"
        :project="project"
        :negative="negative"
        @close="popupVisible = false; selectedPayment = null; sortPaymentsByDate()" 
        @add="addNewPayment"
        @update="updateExistingPayment"
        @delete="deletePayment"
    />
  </div>
</template>

<script>
import { defineComponent, inject } from 'vue';
import PlanOverlay from '@/components/Others/PlanOverlay.vue';
import PopupPayment from '@/components/Popups/PopupPayment.vue';
import PaymentList from '@/components/Cash/PaymentsList.vue';
import PaymentChart from '@/components/Cash/PaymentChart.vue';
import { 
  fetchPayments, addPayment, updatePayment, updateRecurringPayments, deletePayment, addRecurringPayments, deleteRecurringPayments
} from '@/services/paymentService';

export default defineComponent({
  name: 'ShowCash',
  components: {
    PlanOverlay,
    PopupPayment,
    PaymentChart,
    PaymentList,
  },
  props: ['project'],
  data() {
    return {
      payments: [],
      selectedPayment: null,
      negative: false,
      popupVisible: false,
      startDate: this.formatDate(new Date()),
      endDate: this.formatDate(new Date().setDate(new Date().getDate() + 30)),
      selectedYear: 2025,
      selectedMonth: '',
    };
  },
  setup() {
    const perm = inject('userPerm');
    const currentUser = inject('currentUser');
    return { perm, currentUser };
  },
  computed: {
    cashPerm() {
      return this.perm().cash;
    },
    isSubUser() {
      const user = this.currentUser && this.currentUser();
      return user ? !!user.mainUserId : false;
    },
    filteredPayments() {
      if (!this.payments || this.payments.length === 0) {
        return {
          positivePayments: [],
          negativePayments: []
        };
      }

      let sumAt = {};
      let cumul = 0;

      for (const payment of this.payments) {
        if(!payment.isGlobal && !this.project){
          continue;
        }
        
        const date = payment.date;
        const montant = payment.amount * (payment.negative ? -1 : 1);
        cumul += montant;
        sumAt[date] = cumul;
      }

      // Filtrer les paiements par année et mois sélectionnés
      let filtered = this.payments.filter(payment => {
        const paymentDate = new Date(payment.date);

        return (
          paymentDate >= new Date(this.startDate) && 
          paymentDate <= new Date(this.endDate)
        );
      });
      
      if(!this.project){
        filtered = filtered.filter(payment => payment.isGlobal == true);
      }
      
      const payments = {
        allPayments: filtered,
        sumAt: sumAt,
        positivePayments: filtered.filter(payment => payment.negative === false),
        negativePayments: filtered.filter(payment => payment.negative === true)
      };

      console.log("payments", payments);
      return payments;
    }
  },
  methods: {
    formatDate(date) {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0'); // Les mois commencent à 0
      const year = d.getFullYear();
      return `${year}-${month}-${day}`;
    },
    getHeight() {
      return (window.innerHeight - 132) + 'px';
    },
    async addNewPayment(payment) {
      if (!payment) return;
      if (!payment.amount) payment.amount = 0;
      // Selon la fréquence, utiliser la fonction appropriée
      switch (payment.frequency) {
        case 'unique':
          await this.addPayment(payment);
          break;
        case 'mensuel':
          // Utilisation de la fonction addRecurringPayments
          const newPayments = await addRecurringPayments(payment);
          newPayments.forEach(newPayment => {
            this.payments.push(newPayment);
          });
          break;
        case 'trimestriel': {
          const newPayments = await addRecurringPayments(payment, 3);
          newPayments.forEach(newPayment => {
            this.payments.push(newPayment);
          });
          break;
        }
        case 'annuel': {
          const newPayments = await addRecurringPayments(payment, 12);
          newPayments.forEach(newPayment => {
            this.payments.push(newPayment);
          });
          break;
        }
        default:
          console.warn('Fréquence non gérée :', payment.frequency);
      }
      this.sortPaymentsByDate();
    },
    async addPayment(payment) {
      const newPayment = await addPayment(payment);
      this.payments.push(newPayment);
      this.sortPaymentsByDate();
    },
    async updateExistingPayment(updatedPayment) {
      if (!updatedPayment) {
        console.warn("updateExistingPayment : aucun paiement fourni");
        return;
      }
      // Pour un paiement récurrent
      if (updatedPayment.recurringId) {
        // Si les dates ont été modifiées, on regénère la série complète
        if (updatedPayment.date && updatedPayment.dateEnd) {
          // Supprimer la série existante depuis Firestore
          await deleteRecurringPayments(updatedPayment.recurringId);
          // Mettre à jour localement : retirer les paiements de cette série
          this.payments = this.payments.filter(p => p.recurringId !== updatedPayment.recurringId);
          
          // Déterminer le pas de fréquence en fonction de la fréquence
          let frequencyStep = 1;
          if (updatedPayment.frequency === 'trimestriel') frequencyStep = 3;
          else if (updatedPayment.frequency === 'annuel') frequencyStep = 12;
          // Regénérer la série avec les nouvelles dates
          const newPayments = await addRecurringPayments(updatedPayment, frequencyStep);
          newPayments.forEach(newPayment => {
            this.payments.push(newPayment);
          });
        } else {
          // Sinon, mettre à jour les autres champs (excluant date et dateEnd)
          const { date, dateEnd, ...fieldsToUpdate } = updatedPayment;
          await updateRecurringPayments(updatedPayment.recurringId, fieldsToUpdate);
          this.payments = this.payments.map(p =>
            p.recurringId === updatedPayment.recurringId ? { ...p, ...fieldsToUpdate } : p
          );
        }
      } else {
        // Mise à jour d'un paiement unique
        await updatePayment(updatedPayment);
        this.payments = this.payments.map(p =>
          p.id === updatedPayment.id ? { ...p, ...updatedPayment } : p
        );
      }
      this.sortPaymentsByDate();
    },
    sortPaymentsByDate() {
      this.payments.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA - dateB;
      });
    },
    async deletePayment(paymentId) {
      if (!paymentId) return;
      
      // Rechercher le paiement correspondant dans le tableau local
      const payment = this.payments.find(p => p.id === paymentId);
      
      if (payment && payment.recurringId) {
        // Suppression de tous les paiements de la série récurrente
        await deleteRecurringPayments(payment.recurringId);
        // Mettre à jour localement : supprimer tous les paiements de cette série
        this.payments = this.payments.filter(p => p.recurringId !== payment.recurringId);
      } else {
        // Suppression d'un paiement unique
        this.payments = this.payments.filter(p => p.id !== paymentId);
        await deletePayment(paymentId);
      }
    },
  },
  async mounted() {
    this.payments = await fetchPayments();

    if(this.project){
      this.payments = this.payments.filter(payment => payment.projectId == this.project.id);
    }

    this.sortPaymentsByDate();
  },
  watch:{
    'startDate'(newStart) {
			if (this.endDate < newStart) {
				this.endDate = this.formatDate(new Date(newStart).setDate(new Date(newStart).getDate() + 1));
			}
		},
		'endDate'(newEnd) {
			if (newEnd < this.startDate) {
				this.endDate = this.formatDate(new Date(this.startDate).setDate(new Date(this.startDate).getDate() + 1));
			}
		},
  }
});
</script>

<style scoped>

</style>